import type { GuestProfileCategory } from "./aboutGuestsOptions"
import type { StayGuestProfileTraveller } from "./stayData"

type AboutGuestsListProps = {
  category: GuestProfileCategory
  isSaving: boolean
  onEditValues: (category: GuestProfileCategory, traveller: StayGuestProfileTraveller) => void
  travellers: readonly StayGuestProfileTraveller[]
}

export function AboutGuestsList({
  category,
  isSaving,
  onEditValues,
  travellers,
}: AboutGuestsListProps) {
  const groups = travellers
    .map((traveller) => {
      const values = traveller[category.field].filter((value) => value.trim())
      const otherValue = traveller[category.otherField].trim()

      return {
        otherValue,
        traveller,
        values,
      }
    })
    .filter((group) => group.values.length || group.otherValue)

  if (!groups.length) {
    return null
  }

  return (
    <div className="flex flex-col gap-4">
      {groups.map(({ otherValue, traveller, values }) => (
        <section className="flex flex-col gap-2" key={`${category.field}-${traveller.id}`}>
          <div className="flex items-center gap-2">
            <h4 className="min-w-0 flex-1 text-xs font-semibold uppercase leading-4 tracking-[0.72px] text-[#404040]">
              {traveller.display_name}
            </h4>
            {values.length ? (
              <button
                aria-label={`Edit ${category.label} for ${traveller.display_name}`}
                className="group/edit inline-flex h-4 shrink-0 items-center justify-end gap-1 text-[#a3a3a3] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#337ab7] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isSaving}
                onClick={() => onEditValues(category, traveller)}
                type="button"
              >
                <span
                  aria-hidden="true"
                  className="text-xs font-medium leading-4 opacity-0 group-hover/edit:opacity-100 group-focus/edit:opacity-100"
                >
                  Edit
                </span>
                <EditIcon />
              </button>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2.5">
            {values.map((value) => (
              <span
                className="inline-flex h-8 items-center overflow-hidden whitespace-nowrap rounded-[24px] border border-[#d4d4d4] bg-[#f5f5f5] px-3 py-2 text-[13px] font-normal leading-normal text-black"
                key={`${traveller.id}-${category.field}-${value}`}
              >
                {value}
              </span>
            ))}
            {otherValue ? (
              <span className="inline-flex h-8 items-center overflow-hidden whitespace-nowrap rounded-[24px] border border-[#d4d4d4] bg-[#f5f5f5] px-3 py-2 text-[13px] font-normal leading-normal text-black">
                Other: {otherValue}
              </span>
            ) : null}
          </div>
        </section>
      ))}
    </div>
  )
}

function EditIcon() {
  return (
    <svg aria-hidden="true" className="size-4" fill="none" viewBox="0 0 16 16">
      <path
        d="M3.3 11.5 4 13l1.5-.6 6.9-6.9-2.2-2.2-6.9 6.9Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.2"
      />
      <path d="m9.7 3.8 2.1 2.1" stroke="currentColor" strokeLinecap="round" strokeWidth="1.2" />
    </svg>
  )
}
